'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from 'react';
import {
  catalogUi,
  extraUi,
  listsUi,
  resolvePublicUiLocale,
  type CatalogUiStrings,
  type ExtraUiStrings,
  type ListsUiStrings,
} from '@containd/catalog-ui';
import type { CatalogSiteLocale } from '@containd/catalog-ui';

export type PublicUiValue = {
  locale: CatalogSiteLocale;
  catalog: CatalogUiStrings;
  lists: ListsUiStrings;
  extra: ExtraUiStrings;
};

const PublicUiContext = createContext<PublicUiValue>({
  locale: 'en',
  catalog: catalogUi('en'),
  lists: listsUi('en'),
  extra: extraUi('en'),
});

function localeFromPath(): string | null {
  if (typeof window === 'undefined') return null;
  const m = window.location.pathname.match(/^\/(en|de|jp)(?:\/|$)/);
  return m?.[1] ?? null;
}

export function PublicUiProvider({
  locale: localeHint,
  children,
}: {
  locale?: string | null;
  children: ReactNode;
}) {
  const locale = resolvePublicUiLocale(
    localeHint || localeFromPath() || (typeof document !== 'undefined' ? document.documentElement.lang : 'en'),
  );
  const value = useMemo<PublicUiValue>(
    () => ({
      locale,
      catalog: catalogUi(locale),
      lists: listsUi(locale),
      extra: extraUi(locale),
    }),
    [locale],
  );

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  return (
    <PublicUiContext.Provider value={value}>{children}</PublicUiContext.Provider>
  );
}

export function usePublicUi(): PublicUiValue {
  return useContext(PublicUiContext);
}
